<template>
  <aside 
    class="main-sidebar"
    :class="{ 'main-sidebar--expanded': !compact }"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <div class="main-sidebar__content">
      <!-- Logo -->
      <div class="main-sidebar__logo">
        <img :src="logo" :class="{ 'main-sidebar__logo--compact': compact }" alt="ETHICC" />
      </div>

      <!-- Menu items -->
      <div class="main-sidebar__menu">

        <!-- Groupe 2 : Projets et Clients -->
        <div class="menu-group">
          <MainMenuItem 
            v-for="item in menuGroups.projects"
            :key="item.path"
            :to="item.path"
            :compact="compact"
            :active="currentPath.startsWith(item.path)"
            tag="router-link"
          >
            <template #icon>
              <Icon :name="item.icon" :size="20" />
            </template>
            {{ item.name }}
          </MainMenuItem>
        </div>

        <hr v-if="menuGroups.settings.length > 0" class="menu-divider" />

        <!-- Groupe 3 : Paramètres -->
        <div class="menu-group">
          <MainMenuItem 
            v-for="item in menuGroups.settings"
            :key="item.path"
            :to="item.path"
            :compact="compact"
            :active="currentPath.startsWith(item.path)"
            tag="router-link"
          >
            <template #icon>
              <Icon :name="item.icon" :size="20" />
            </template>
            {{ item.name }}
          </MainMenuItem>
        </div>
      </div>

      <!-- Version -->
      <div class="env-info" v-if="!compact">
        <div class="env-details">
          <span class="app-name">Ethicc</span>
          <span class="version">v{{ version }}</span>
        </div>
      </div>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import MainMenuItem from '@/components/menu/MainMenuItem.vue'
import Icon from '@/components/icons/Icon.vue'
import logo from '@/assets/ethicc_logo.svg?url'
import { version } from '../../../package.json'

defineProps<{
  compact?: boolean
}>()

const route = useRoute()
const currentPath = computed(() => route.path)
const isHovered = ref(false)

// Use auth store reactive values (no fetching here)
const authStore = useAuthStore()
// Admin = role === 1; hide admin items while loading or if role unknown
const isAdmin = computed(() => authStore.userRole === 1)
const canShowAdmin = computed(() => !authStore.loading && authStore.userRole !== null && isAdmin.value)

// Admin items depend purely on store state; no side-effects in the component
const menuGroups = computed(() => ({
  projects: [
    { name: 'Projets', path: '/projects', icon: 'building' },
    { name: 'Clients', path: '/clients', icon: 'users' }
  ],
  settings: canShowAdmin.value ? [
    { name: 'Administration', path: '/admin', icon: 'settings' }
  ] : []
}))

const handleMouseEnter = () => {
  isHovered.value = true
}

const handleMouseLeave = () => {
  isHovered.value = false
}
</script>

<style scoped>
.main-sidebar {
  /* Layout commun */
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  flex-shrink: 0;
  background: var(--layout-sidebar-bg);
  height: 100vh;
  transition: all 0.3s ease;
  position: absolute;
  z-index: 100;
  border-right: 1px solid var(--layout-sidebar-border);

  /* Mode rétracté par défaut */
  width: 56px;
}

/* Mode étendu */
.main-sidebar--expanded {
  width: 220px;
  box-shadow: 4px 4px 8px 0px var(--effect-shadow);
}

.main-sidebar__content {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  height: 100%;
}

.main-sidebar__logo {
  display: flex;
  padding: 20px 18px;
  justify-content: flex-start;
  align-items: center;
  transition: all 0.3s ease;
}

.main-sidebar__logo img {
  width: auto;
  height: 20px;
  transition: all 0.3s ease;
  filter: brightness(0) invert(1);
}

.main-sidebar__menu {
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  padding: 0 8px;
  transition: all 0.3s ease;
}

.menu-group {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 4px;
}

.menu-divider {
  width: 100%;
  margin: 8px 0;
  border: none;
  height: 1px;
  background-color: var(--layout-sidebar-border);
}

.env-info {
  margin-top: auto;
  padding: 16px;
  width: 100%;
}

.env-details {
  display: flex;
  flex-direction: row;
  gap: 4px;
  font-size: 12px;
}

.app-name {
  color: var(--layout-sidebar-text-muted);
  font-weight: 600;
}

.version {
  color: var(--layout-sidebar-text-muted);
}
</style>